import { X } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import SpotlightArt from "./SpotlightArt";

/** O selo de cima do cartão: o que a pessoa lê antes do título. */
export const BADGE_LABELS = Object.freeze({ novidade: "Novidade", experimente: "Experimente" });

/**
 * O destaque de lançamento da tela inicial: um cartão largo com o selo (Novidade ou Experimente), o título, a descrição, o
 * botão e, ao lado, a arte animada ou a mídia. Só desenha: quem decide quando aparece, grava "visto" e navega é
 * `useHomeSpotlight`. O admin usa o mesmo componente na pré-visualização, então o que ele vê é o que a pessoa recebe.
 *
 * "Fechar" é o X no canto (some de vez, como no cartão do menu); o botão grande é a ação.
 *
 * @param {{
 *   title: string, body: string, badge?: "novidade"|"experimente", ctaLabel?: string,
 *   artKey?: string|null, mediaUrl?: string|null, mediaType?: string|null,
 *   titleId?: string, onTry?: () => void, onDismiss?: () => void,
 * }} props
 */
export default function HomeSpotlightCard({
  title,
  body,
  badge = "novidade",
  ctaLabel = "Experimente",
  artKey = null,
  mediaUrl = null,
  mediaType = null,
  titleId,
  onTry,
  onDismiss,
}) {
  const { resolved } = useTheme();
  const dark = resolved !== "light";

  return (
    <section
      aria-labelledby={titleId}
      data-testid="home-spotlight"
      className="relative mb-4 grid overflow-hidden rounded-[22px] border border-black/[.06] bg-[linear-gradient(140deg,rgba(6,182,212,.20),rgba(120,110,255,.12)_58%,rgba(255,255,255,.85))] dark:border-transparent dark:bg-[linear-gradient(140deg,rgba(6,182,212,.18),rgba(120,110,255,.10)_58%,rgba(255,255,255,.028))] md:grid-cols-[1.15fr_1fr]"
    >
      <div className="flex min-w-0 flex-col px-6 py-6 md:py-7">
        <span className="font-mono text-[9.5px] font-medium uppercase leading-none tracking-[.14em] text-[#6c6f80] dark:text-white/[.5]">
          <span aria-hidden="true" className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-[#06b6d4] align-middle" />
          {BADGE_LABELS[badge] ?? BADGE_LABELS.novidade}
        </span>
        <h2 id={titleId} className="mt-3 text-[22px] font-bold leading-[1.2] tracking-[-.026em] text-[#15161f] dark:text-white">
          {title}
        </h2>
        <p className="mt-2 max-w-[46ch] text-[14px] leading-[1.5] text-[#4a4c5c] dark:text-white/[.66]">{body}</p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onTry}
            className="inline-flex h-9 flex-none items-center whitespace-nowrap rounded-[18px] bg-[#15161f] px-[18px] text-[12.5px] font-bold text-white transition-opacity hover:opacity-90 dark:bg-white dark:text-[#0a0a0f]"
          >
            {ctaLabel}
          </button>
        </div>
      </div>

      <div aria-hidden={artKey ? undefined : "true"} className="relative min-h-[170px] min-w-0 px-6 pb-6 md:px-4 md:py-5">
        <div className="mx-auto h-full max-h-[210px] min-h-[150px] w-full max-w-[360px]">
          <SpotlightArt artKey={artKey} mediaUrl={mediaUrl} mediaType={mediaType} dark={dark} title={title} />
        </div>
      </div>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fechar a novidade"
        className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-[#4a4c5c] transition-colors hover:bg-black/[.06] dark:text-white/[.66] dark:hover:bg-white/10"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>
  );
}
